// ─────────────────────────────────────────────────────────────
// ZIFEK — Badge de note moyenne d'un commerce / vendeur.
// Réutilisé partout où l'on veut rendre le CLASSEMENT visible :
// cartes du chat IA, cartes vendeurs de la marketplace, dashboard.
// La note provient des avis publiés (lib/vendorReviews).
// ─────────────────────────────────────────────────────────────

export interface VendorRatingBadgeProps {
  /** Note moyenne (0 à 5). 0 ou absent = aucun avis. */
  average: number;
  /** Nombre d'avis. */
  count?: number;
  /** Taille des étoiles. */
  size?: 'sm' | 'md';
  /** Afficher le nombre d'avis entre parenthèses. */
  showCount?: boolean;
  /** Libellé affiché quand il n'y a encore aucun avis. */
  emptyLabel?: string;
  className?: string;
}

/**
 * Étoiles de notation compactes : note moyenne + volume d'avis.
 * Sert de signal de classement sur toutes les cartes de la plateforme.
 */
export default function VendorRatingBadge({
  average,
  count = 0,
  size = 'sm',
  showCount = true,
  emptyLabel = 'Nouveau',
  className = '',
}: VendorRatingBadgeProps) {
  const starSize = size === 'md' ? 'text-sm' : 'text-xs';

  if (!average || average <= 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-medium text-foreground-400 ${className}`}
        title="Aucun avis pour le moment"
      >
        <i className={`ri-star-line ${starSize}`}></i>
        {emptyLabel}
      </span>
    );
  }

  const rounded = Math.round(average);
  const note = `${average.toFixed(1)} / 5${count > 0 ? ` · ${count} avis` : ''}`;

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${className}`}
      title={note}
      aria-label={note}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`${n <= rounded ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} ${starSize}`}
        ></i>
      ))}
      <span className="text-[10px] font-semibold text-foreground-700 ml-0.5">{average.toFixed(1)}</span>
      {showCount && count > 0 && (
        <span className="text-[10px] text-foreground-400">({count})</span>
      )}
    </span>
  );
}